/**
 * Table of contents of an article, drawn from the body's own headings into the side column.
 *
 * The page is whole without this file: the headings are in the body, and the rail it fills only exists
 * from 1200 px (layouts/_article-page.scss). The script adds the anchors the stored HTML does not carry,
 * a list of links to them, and the mark of the section being read.
 *
 * The rail is ONE box: two sticky elements in one grid cell would overlap, so the table of contents and
 * the series summary, when the article has one, share the box.
 * The summary is moved into it as rendered — its markup does not change.
 */

const MIN_HEADINGS = 3;

const LABELS = {
	fr: 'Dans cet article',
	en: 'In this article',
};

function label() {
	const lang = (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
	return LABELS[lang] || LABELS.fr;
}

function slug(text) {
	return text
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 64) || 'section';
}

function anchor(heading, taken) {
	if (heading.id) return heading.id;
	const base = slug(heading.textContent);
	let id = base;
	for (let n = 2; taken.has(id) || document.getElementById(id); n++) id = `${base}-${n}`;
	taken.add(id);
	heading.id = id;
	return id;
}

function build(read, headings) {
	const nav = document.createElement('nav');
	nav.className = 'arcate-article-toc';
	nav.setAttribute('aria-labelledby', 'arcate-article-toc-title');

	const title = document.createElement('p');
	title.className = 'arcate-article-toc-kicker';
	title.id = 'arcate-article-toc-title';
	title.textContent = label();

	const list = document.createElement('ol');
	list.className = 'arcate-article-toc-list';

	const taken = new Set();
	const links = headings.map((heading) => {
		const item = document.createElement('li');
		item.className = 'arcate-article-toc-item';
		const link = document.createElement('a');
		link.className = 'arcate-article-toc-link';
		link.href = `#${anchor(heading, taken)}`;
		link.textContent = heading.textContent.trim();
		item.append(link);
		list.append(item);
		return link;
	});

	nav.append(title, list);

	const rail = document.createElement('div');
	rail.className = 'arcate-article-rail';
	rail.append(nav);
	const series = read.querySelector(':scope > .ui-section.kind-series-nav');
	if (series) rail.append(series);
	read.append(rail);

	return links;
}

// The section being read is the last one whose heading has passed a line a third down the viewport.
function follow(headings, links) {
	let current = -1;
	let pending = false;

	const update = () => {
		pending = false;
		const line = window.innerHeight / 3;
		let index = -1;
		for (let i = 0; i < headings.length; i++) {
			if (headings[i].getBoundingClientRect().top <= line) index = i;
			else break;
		}
		if (index === current) return;
		if (current >= 0) links[current].removeAttribute('aria-current');
		if (index >= 0) links[index].setAttribute('aria-current', 'location');
		current = index;
	};

	const schedule = () => {
		if (pending) return;
		pending = true;
		requestAnimationFrame(update);
	};

	window.addEventListener('scroll', schedule, { passive: true });
	window.addEventListener('resize', schedule, { passive: true });
	update();
}

export function prepareArticleToc(root = document) {
	const read = root.querySelector('main[role="main"] > .arcate-article-read');
	if (!read || read.querySelector('.arcate-article-toc')) return;
	const body = read.querySelector(':scope > .ui-section > .ui-container > .ui-content.kind-body');
	if (!body) return;
	const headings = [...body.querySelectorAll(':scope > h2')].filter((h) => h.textContent.trim());
	if (headings.length < MIN_HEADINGS) return;
	follow(headings, build(read, headings));
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => prepareArticleToc());
} else {
	prepareArticleToc();
}
